import { watch, onBeforeUnmount, Ref } from 'vue';

/**
 * Locks page scroll while modal or drawer entries are open.
 * Also prevents right-anchored drawers from shifting when the body scrollbar
 * disappears, then restores the previous inline scroll styles on unlock.
 */
export default function useBodyScrollLock(openCount: Ref<number>) {
    let previousBodyOverflow: string | null = null;
    let previousRootOverflowY: string | null = null;
    let previousRootScrollbarGutter: string | null = null;

    const lock = () => {
        if (typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;
        if (previousBodyOverflow === null) {
            previousBodyOverflow = document.body.style.overflow;
            previousRootOverflowY = root.style.overflowY;
            previousRootScrollbarGutter = root.style.scrollbarGutter;
        }

        // Measure the scrollbar's layout width before hiding it.
        const scrollbarWidth = globalThis.window.innerWidth - root.clientWidth;
        if (scrollbarWidth > 0) {
            // `scrollbar-gutter` only reserves space on a scroll container, so make
            // the root one explicitly before hiding the body scrollbar.
            root.style.overflowY = 'auto';
            root.style.scrollbarGutter = 'stable';
        }
        document.body.style.overflow = 'hidden';
    };

    const unlock = () => {
        if (typeof document === 'undefined' || previousBodyOverflow === null) {
            return;
        }

        const root = document.documentElement;
        document.body.style.overflow = previousBodyOverflow;
        root.style.overflowY = previousRootOverflowY ?? '';
        root.style.scrollbarGutter = previousRootScrollbarGutter ?? '';
        previousBodyOverflow = null;
        previousRootOverflowY = null;
        previousRootScrollbarGutter = null;
    };

    watch(
        () => openCount.value,
        (count) => {
            if (count > 0) {
                lock();
                return;
            }

            unlock();
        },
        { immediate: true }
    );

    onBeforeUnmount(unlock);

    return { lock, unlock };
}
